import * as React from "react"
import {
  DimensionSortMode,
  type Statement,
  useReadReportStatementComponentDataQuery,
  type ViewIdentifierInput,
} from "@digits-graphql/frontend/graphql-bearer"
import useConstant from "@digits-shared/hooks/useConstant"
import { isAgingReportKind } from "src/frontend/components/OS/Applications/Reports/Packages/shared"
import { useStatementLiveData } from "src/frontend/components/Shared/Layout/Components/Statements/useStatementLiveData"
import { type MatchedConfig } from "src/frontend/components/Shared/Layout/types"
import usePreviousDistinct from "src/shared/hooks/usePreviousDistinct"

/**
 * Must be used within a LayoutContext.
 */
export function useStatementComponentData(
  config: MatchedConfig<"statement">,
  viewId: ViewIdentifierInput,
  layoutId: string | undefined | null,
  dataId: string | undefined | null,
  skipAll?: boolean
) {
  const { data, loading, error } = useReadReportStatementComponentDataQuery({
    variables: {
      dataId: dataId ?? "",
      layoutId: layoutId ?? "",
      legalEntityId: viewId.legalEntityId,
      limit: 5,
      sort: DimensionSortMode.Total,
      timeHistory:
        !config.statement.options?.categoryByDepartmentOptions &&
        !config.statement.options?.categoryByLocationOptions,
      departmentHistory: Boolean(config.statement.options?.categoryByDepartmentOptions),
      locationHistory: Boolean(config.statement.options?.categoryByLocationOptions),
    },
    skip: !dataId || !layoutId || skipAll,
    // Disable caching to prevent memory leaks from accumulating statement data
    fetchPolicy: "no-cache",

    // LEs with large COA can take 1+ seconds to load a single statement.
    // Stop batching this request to unblock all the other components
    //
    // NOTE (4/9/25) test loading all components at the same to test locking issues (no more lazy loading)
    // context: { noBatch: true },
  })
  const previousData = usePreviousDistinct(data)

  const initialDataId = useConstant(dataId)
  // Skip loading live data if this component received a dataId at creation time.
  // This means the component was loaded from archive and should only ever show
  // archived data.
  const skipLiveData = (!!dataId && dataId === initialDataId) || skipAll

  const {
    statement,
    loading: liveLoading,
    error: liveError,
  } = useStatementLiveData(config.statement, config.type, viewId, {
    skip: skipLiveData,
    noLaterThanToday: isAgingReportKind(config.statement.kind),
  })

  return React.useMemo(() => {
    const componentData = data?.readComponentData.data ?? previousData?.readComponentData.data

    // Data read from the archive takes precedence.
    // Resolves the component data union types to the portions we care about.
    if (componentData?.__typename === "Statement") {
      return { statement: componentData as Statement, loading, error }
    }

    // Fall back to live data if we have fetched it.
    // This is necessary to keep data from disappearing mid-drag while we archive the real component data.
    if (statement) {
      return { statement, loading: liveLoading, error: liveError }
    }

    return { statement: undefined, loading, error }
  }, [
    data?.readComponentData.data,
    previousData?.readComponentData.data,
    statement,
    liveLoading,
    loading,
    error,
    liveError,
  ])
}
